import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../lib/utils";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [device, setDevice] = useState(null); // null = a verificar; {unlocked, profiles, ...}
  const [loading, setLoading] = useState(true);
  // Sessão vive num cookie HttpOnly (não acessível a scripts). `token` mantém-se por compatibilidade com componentes antigos (sempre null).
  const token = null;

  useEffect(() => {
    localStorage.removeItem("auth_token");
  }, []);

  const fetchMe = async () => {
    const response = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const refreshDevice = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/auth/device`, { withCredentials: true });
      setDevice(r.data);
      return r.data;
    } catch {
      const d = { unlocked: false, profiles: [] };
      setDevice(d);
      return d;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const [me] = await Promise.allSettled([fetchMe(), refreshDevice()]);
    if (me.status === "rejected") setUser(null);
    setLoading(false);
  }, [refreshDevice]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const afterLogin = (data) => {
    setUser(data.user);
    if (data.user?.password_expired) {
      toast.warning("A sua palavra-passe expirou (política de renovação periódica). Altere-a em Segurança.", { duration: 10000, action: { label: "Alterar", onClick: () => (window.location.href = "/security") } });
    }
    if (data.device_unlocked) refreshDevice();
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    if (response.data.requires_2fa) {
      return response.data;
    }
    afterLogin(response.data);
    return response.data;
  };

  const verify2FA = async (tempToken, code) => {
    const response = await axios.post(
      `${API_URL}/auth/login/2fa`,
      { temp_token: tempToken, code },
      { withCredentials: true }
    );
    afterLogin(response.data);
    return response.data;
  };

  // Entrada por perfil + PIN (dispositivo desbloqueado por um admin/gerente)
  const pinLogin = async (userId, pin) => {
    const response = await axios.post(`${API_URL}/auth/pin-login`, { user_id: userId, pin }, { withCredentials: true });
    setUser(response.data.user);
    refreshDevice();
    return response.data;
  };

  // Termina a sessão do utilizador mas mantém o dispositivo desbloqueado (volta ao ecrã de perfis)
  const switchUser = async () => {
    try {
      await axios.post(`${API_URL}/auth/switch-user`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setUser(null);
    await refreshDevice();
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    return response.data;
  };

  const logout = async (lockDevice = false) => {
    try {
      await axios.post(`${API_URL}/auth/logout${lockDevice ? "?lock_device=true" : ""}`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    setUser(null);
    if (lockDevice) setDevice({ unlocked: false, profiles: [] });
    else refreshDevice();
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/auth/session?session_id=${sessionId}`,
      {},
      { withCredentials: true }
    );
    setUser(response.data.user);
    return response.data;
  };

  const refreshUser = async () => {
    try {
      return await fetchMe();
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  const value = {
    user,
    token,
    loading,
    device,
    deviceUnlocked: !!device?.unlocked,
    refreshDevice,
    pinLogin,
    switchUser,
    login,
    verify2FA,
    register,
    logout,
    loginWithGoogle,
    exchangeSession,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isManager: ["admin", "manager", "director"].includes(user?.role),
    modules: user?.modules || [],
    hasModule: (m) => (user?.modules || []).includes("*") || (user?.modules || []).includes(m),
    needsCompanySetup: user?.needs_company_setup || false,
    companyId: user?.company_id,
    companyName: user?.company_name,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
